import React, { useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { Modal } from '../../../shared/Modal';
import { Button } from '../../../shared/Button';
import { useToast } from '../../../shared/Toast';
import { useAccount } from '../../../../hooks/useAccount';
import { useSchedulingOptions } from '../../../../hooks/useSchedulingOptions';
import { SchedulingAccordion } from '../../SchedulingAccordion';
import { ModalActionFooter } from '../sections/ModalActionFooter';
import { DeleteAccordion } from '../sections/DeleteAccordion';

/**
 * VideoMetadataCard Component
 *
 * Displays video metadata in a clean card format
 */
function VideoMetadataCard({ video, t }) {
  const formatDuration = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return null;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  return (
    <div className="bg-gray-50 rounded-lg p-4 space-y-2 text-sm">
      {/* Duration - always shown */}
      <div className="flex justify-between">
        <span className="text-gray-600">
          {t('modal.video.duration', { defaultValue: 'Duration' })}:
        </span>
        <span className="font-medium">{formatDuration(video.duration)}</span>
      </div>

      {/* File Size */}
      {video.file_size && (
        <div className="flex justify-between">
          <span className="text-gray-600">
            {t('modal.video.fileSize', { defaultValue: 'File Size' })}:
          </span>
          <span className="font-medium">{formatFileSize(video.file_size)}</span>
        </div>
      )}

      {/* Resolution */}
      {video.resolution && (
        <div className="flex justify-between">
          <span className="text-gray-600">
            {t('modal.video.resolution', { defaultValue: 'Resolution' })}:
          </span>
          <span className="font-medium">{video.resolution}</span>
        </div>
      )}

      {/* Codec */}
      {video.codec && (
        <div className="flex justify-between">
          <span className="text-gray-600">
            {t('modal.video.codec', { defaultValue: 'Codec' })}:
          </span>
          <span className="font-medium">{video.codec}</span>
        </div>
      )}

      {/* FPS */}
      {video.fps && (
        <div className="flex justify-between">
          <span className="text-gray-600">
            {t('modal.video.fps', { defaultValue: 'FPS' })}:
          </span>
          <span className="font-medium">{video.fps.toFixed(1)}</span>
        </div>
      )}

      {/* Audio */}
      {video.has_audio !== undefined && (
        <div className="flex justify-between">
          <span className="text-gray-600">
            {t('modal.video.audio', { defaultValue: 'Audio' })}:
          </span>
          <span className="font-medium">{video.has_audio ? 'Yes' : 'No'}</span>
        </div>
      )}
    </div>
  );
}

/**
 * VideoApprovalModal Component
 *
 * Modal for approving or deleting uploaded videos.
 * Simpler than ImageApprovalModal as videos don't have
 * caption editing, style changes, or GIF stickers.
 *
 * Features:
 * - Video preview player
 * - Video metadata display
 * - Scheduling options
 * - Delete functionality
 */
export function VideoApprovalModal({
  item,
  isOpen,
  onClose,
  onApproveVideo,
  onDeleteVideo,
}) {
  const { t } = useTranslation('approval');
  const toast = useToast();
  const { selectedAccountId } = useAccount();

  // Scheduling options
  const scheduling = useSchedulingOptions(selectedAccountId, isOpen);

  // UI State
  const [isApprovingVideo, setIsApprovingVideo] = useState(false);
  const [isDeletingVideo, setIsDeletingVideo] = useState(false);

  /**
   * Handle video approval
   */
  const handleApproveVideoClick = useCallback(async () => {
    setIsApprovingVideo(true);
    try {
      if (!item?.video_id) {
        throw new Error('Invalid video: missing video_id');
      }

      if (onApproveVideo) {
        await Promise.resolve(onApproveVideo(item));
        toast.success(
          t('modal.toast.videoApproved', { defaultValue: 'Video approved!' })
        );
      } else {
        throw new Error('No approval callback provided');
      }

      onClose();
    } catch (error) {
      console.error('Video approval error:', error);
      toast.error(
        t('modal.toast.videoApprovalError', {
          defaultValue: 'Failed to approve video',
          error: error.message,
        })
      );
    } finally {
      setIsApprovingVideo(false);
    }
  }, [item, onApproveVideo, onClose, toast, t]);

  /**
   * Handle video deletion
   */
  const handleDeleteVideoClick = useCallback(async () => {
    setIsDeletingVideo(true);
    try {
      if (!item?.video_id) {
        throw new Error('Invalid video: missing video_id');
      }

      if (onDeleteVideo) {
        await Promise.resolve(onDeleteVideo(item));
        toast.success(
          t('modal.toast.videoDeleted', { defaultValue: 'Video deleted' })
        );
      } else {
        throw new Error('No delete callback provided');
      }

      onClose();
    } catch (error) {
      console.error('Video deletion error:', error);
      toast.error(
        t('modal.toast.videoDeletionError', {
          defaultValue: 'Failed to delete video',
          error: error.message,
        })
      );
    } finally {
      setIsDeletingVideo(false);
    }
  }, [item, onDeleteVideo, onClose, toast, t]);

  /**
   * Handle modal close
   */
  const handleClose = useCallback(() => {
    onClose();
  }, [onClose]);

  if (!item) return null;

  const isProcessing = isApprovingVideo || isDeletingVideo;

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      size="5xl"
      title={item.file_name || t('modal.title')}
      closeOnBackdrop={false}
      closeOnEscape={!isProcessing}
    >
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 -mx-6 -my-4 lg:h-[calc(100vh-160px)]">
        {/* Left Column - Video Preview */}
        <div className="lg:sticky lg:top-0 lg:self-start bg-gray-50 p-6 lg:h-[calc(100vh-160px)] flex items-center justify-center">
          <div className="w-full max-w-sm">
            <h3 className="text-sm font-medium text-gray-700 mb-3 text-center">
              {t('modal.preview.title')}
            </h3>
            <div className="bg-gray-900 rounded-lg overflow-hidden">
              <video
                src={item.blob_url}
                controls
                className="w-full h-auto"
                style={{ aspectRatio: '9/16', maxHeight: 'calc(100vh - 280px)' }}
              >
                Your browser does not support the video tag.
              </video>
            </div>
          </div>
        </div>

        {/* Right Column - Content & Controls */}
        <div className="flex flex-col lg:h-[calc(100vh-160px)]">
          {/* Scrollable Content */}
          <div className="flex-1 p-6 space-y-6 overflow-y-auto overscroll-contain">
            {/* Video Metadata */}
            <VideoMetadataCard video={item} t={t} />

            {/* Info: Editing not available for videos yet */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-sm text-blue-800">
              <p>
                {t('modal.video.editingNotAvailable', {
                  defaultValue:
                    'Video editing features will be available in a future release. For now, you can approve or delete this video.',
                })}
              </p>
            </div>

            {/* Scheduling Accordion */}
            <SchedulingAccordion
              autoScheduledTime={scheduling.autoScheduledTime}
              manualScheduledTime={scheduling.manualScheduledTime}
              scheduledTime={scheduling.scheduledTime}
              isManualMode={scheduling.isManualMode}
              cascadeInfo={scheduling.cascadeInfo}
              postIntervalHours={scheduling.postIntervalHours}
              isLoading={scheduling.isLoading}
              error={scheduling.error}
              onManualTimeChange={scheduling.setManualTime}
              onClearManualTime={scheduling.clearManualTime}
              disabled={isProcessing}
            />

            {/* Delete Video Accordion */}
            <DeleteAccordion
              variant="video"
              onDelete={handleDeleteVideoClick}
              isDeleting={isDeletingVideo}
              disabled={isApprovingVideo}
            />
          </div>

          {/* Sticky Action Footer */}
          <ModalActionFooter
            hasChanges={false}
            isProcessing={isApprovingVideo}
            disabled={isDeletingVideo}
            onCancel={handleClose}
            onPrimaryAction={handleApproveVideoClick}
            primaryLabel={t('modal.button.approve', { defaultValue: 'Approve & Post' })}
            processingLabel={t('modal.button.approving', { defaultValue: 'Approving...' })}
          />
        </div>
      </div>
    </Modal>
  );
}

VideoApprovalModal.propTypes = {
  item: PropTypes.shape({
    video_id: PropTypes.string.isRequired,
    file_name: PropTypes.string,
    blob_url: PropTypes.string,
    duration: PropTypes.number,
    file_size: PropTypes.number,
    resolution: PropTypes.string,
    codec: PropTypes.string,
    fps: PropTypes.number,
    has_audio: PropTypes.bool,
  }),
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onApproveVideo: PropTypes.func,
  onDeleteVideo: PropTypes.func,
};

export default VideoApprovalModal;
